import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Bell, Calendar, Tent, Award, AlertCircle, Clock, X, ChevronRight, Loader2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  event_booking:       { icon: Calendar,    color: 'text-cameroon-yellow', bg: 'bg-cameroon-yellow/10', label: 'Event Booking',       link: '/admin/activities' },
  tent_reservation:    { icon: Tent,        color: 'text-teal-400',        bg: 'bg-teal-400/10',        label: 'Tent Reservation',    link: '/admin/activities' },
  sponsor_application: { icon: Award,       color: 'text-purple-400',      bg: 'bg-purple-400/10',      label: 'Sponsor Application', link: '/admin/activities' },
  tent_info_needed:    { icon: AlertCircle, color: 'text-orange-400',      bg: 'bg-orange-400/10',      label: 'Info Needed (Tent)',   link: '/admin/activities' },
  sponsor_info_needed: { icon: AlertCircle, color: 'text-orange-400',      bg: 'bg-orange-400/10',      label: 'Info Needed (Sponsor)', link: '/admin/activities' },
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/notifications`);
      setData(res.data);
    } catch {
      // silenced
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const total = data?.total || 0;
  const counts = data?.counts || {};

  const handleItemClick = (item) => {
    const cfg = TYPE_CONFIG[item.type];
    if (cfg?.link) navigate(cfg.link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-muted transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-cameroon-red text-white text-[10px] font-bold flex items-center justify-center animate-pulse" data-testid="notification-count">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-popover border rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
          {/* Header */}
          <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-cameroon-green" />
              <span className="font-semibold text-sm">Notifications</span>
              {total > 0 && <Badge className="bg-cameroon-red text-white text-[10px] px-1.5 py-0">{total}</Badge>}
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-muted rounded">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Summary counters */}
          {total > 0 && (
            <div className="px-4 py-2 border-b flex flex-wrap gap-2" data-testid="notification-summary">
              {counts.event_bookings > 0 && (
                <Badge variant="outline" className="gap-1 text-xs"><Calendar className="w-3 h-3 text-cameroon-yellow" />{counts.event_bookings} Events</Badge>
              )}
              {counts.tent_reservations > 0 && (
                <Badge variant="outline" className="gap-1 text-xs"><Tent className="w-3 h-3 text-teal-400" />{counts.tent_reservations} Tents</Badge>
              )}
              {counts.sponsor_applications > 0 && (
                <Badge variant="outline" className="gap-1 text-xs"><Award className="w-3 h-3 text-purple-400" />{counts.sponsor_applications} Sponsors</Badge>
              )}
              {counts.info_needed > 0 && (
                <Badge variant="outline" className="gap-1 text-xs"><AlertCircle className="w-3 h-3 text-orange-400" />{counts.info_needed} Info Needed</Badge>
              )}
            </div>
          )}

          {/* Items list */}
          <ScrollArea className="max-h-[400px]">
            {loading && !data && (
              <div className="p-8 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {data && total === 0 && (
              <div className="p-8 text-center" data-testid="notification-empty">
                <div className="w-12 h-12 rounded-full bg-cameroon-green/10 flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-6 h-6 text-cameroon-green" />
                </div>
                <p className="font-medium text-sm">All caught up!</p>
                <p className="text-xs text-muted-foreground mt-1">No pending items that need your attention.</p>
              </div>
            )}

            {data?.items?.map((item) => {
              const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.event_booking;
              const Icon = cfg.icon;
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => handleItemClick(item)}
                  className="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors flex items-start gap-3 group"
                  data-testid={`notification-item-${item.type}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{cfg.label}</Badge>
                      {item.ref && <span className="text-[10px] font-mono text-muted-foreground">{item.ref}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(item.created_at)}</span>
                    <ChevronRight className="w-3 h-3 text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              );
            })}
          </ScrollArea>

          {/* Footer */}
          {total > 0 && (
            <div className="px-4 py-2 border-t bg-muted/30">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-foreground"
                onClick={() => { navigate('/admin/activities'); setOpen(false); }}
                data-testid="view-all-notifications"
              >
                View All in Activities
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
